import React, { useId } from 'react';
import { Box } from '@mui/material';
import { InterventionLadderStepModel } from '../../../api-service/clients';
import { intensityGradient, stepBackgrounds } from './intervention-ladder-colors';

const staircaseHeight = 150;
const markerSize = 30;
// The space above the highest marker and below the lowest is the same, together with the space around the staircase.
const markerInset = 4;
const highestLine = markerInset + markerSize / 2;
const lowestLine = staircaseHeight - markerInset - markerSize / 2;
const markerColor = stepBackgrounds[stepBackgrounds.length - 1];

export interface InterventionLadderStaircaseProps {
  steps: InterventionLadderStepModel[];
  selectedStep: number | undefined;
}

export function InterventionLadderStaircase({ steps, selectedStep }: InterventionLadderStaircaseProps) {
  const gradientId = useId();
  const stepWidth = 1000 / steps.length;
  const rise = steps.length > 1 ? (lowestLine - highestLine) / (steps.length - 1) : 0;

  function lineHeight(index: number) {
    return lowestLine - index * rise;
  }

  const path = steps.map((_, index) => `${index === 0 ? `M0,${lineHeight(0)}` : `V${lineHeight(index)}`} H${(index + 1) * stepWidth}`).join(' ');

  return (
    <Box sx={{ position: 'relative', height: staircaseHeight, mb: 2 }} aria-hidden>
      <svg viewBox={`0 0 1000 ${staircaseHeight}`} preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
            {intensityGradient.map((color, index) => (
              <stop key={color} offset={index / (intensityGradient.length - 1)} stopColor={color} />
            ))}
          </linearGradient>
        </defs>
        <path d={path} fill="none" stroke={`url(#${gradientId})`} strokeWidth={5} strokeLinejoin="miter" vectorEffect="non-scaling-stroke" />
      </svg>
      {steps.map((step, index) => (
        <Box
          key={step.step}
          sx={{
            position: 'absolute',
            left: `${((index + 0.5) / steps.length) * 100}%`,
            top: lineHeight(index),
            transform: 'translate(-50%, -50%)',
            width: markerSize,
            height: markerSize,
            borderRadius: '50%',
            bgcolor: step.step === selectedStep ? markerColor : '#ffffff',
            border: `2px solid ${markerColor}`,
            color: step.step === selectedStep ? '#ffffff' : markerColor,
            transition: 'background-color 150ms, color 150ms',
            fontSize: '0.875rem',
            fontWeight: 700,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          {step.step}
        </Box>
      ))}
    </Box>
  );
}
